import React, { useState, useEffect } from 'react';
import {
  Alert,
  CardAvatar,
  CardHeader,
  CardProgress,
  Spin,
  VerticalSpace,
} from '@brokerbay/design-components';
import { Button, message } from 'components';
import { OnboardingController } from 'api';
import { SafetyCertificateFilled, MailFilled } from '@ant-design/icons';
import styled from 'styled-components';
import { usePusher } from 'hooks';
import type {
  IOnboardingGetBrokerageApproverResponse,
  IOnboardingRequestBrokerageSetupApprovalBody,
} from '@/app-package';
import { useOrgOnboardingContext } from '../../../contexts/OrgOnboardingContext';
import { useUserOnboardingContext } from '../../../contexts/UserOnboardingContext';
import VerificationCard, { VerificationStatus } from './VerificationCard';
import { UserOnboardingStep } from '../../../constants';
import NoApproverView from './NoApproverView';
import { LoadingView } from '../../common/LoadingView';

export type RequestApprovalViewProps = {
  goBackStep: (
    isLicensedUnclaimed: boolean,
    claimHash: string,
  ) => () => void | null;
  handleGoNextStep: (nextStep: UserOnboardingStep) => void;
};

type VerificationStatusStyle = {
  buttonText: string;
  onButtonClick: () => void;
  title: string;
  alert: string;
};

type Approver = IOnboardingGetBrokerageApproverResponse;

type Verified = { status: VerificationStatus; requestedAt?: Date };

const RequestApprovalView = ({
  goBackStep,
  handleGoNextStep,
}: RequestApprovalViewProps) => {
  const { orgClaimHash, orgProfile, getSearchParam } =
    useOrgOnboardingContext();
  const { userProfile } = useUserOnboardingContext();
  const [approver, setApprover] = useState<Approver | undefined>();
  const [approverError, setApproverError] = useState(false);
  const [verified, setVerified] = useState<Verified | undefined>();
  const [emailLoading, setEmailLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const approverTerm =
    orgProfile?.overrideLabel || approver?.title || 'Broker of Record';
  const verifyHash = getSearchParam('verifyHash');
  const submitterEmail = getSearchParam('email') || userProfile?.email;
  const submitterName =
    getSearchParam('name') ||
    `${userProfile?.firstName} ${userProfile?.lastName}`;
  const submitterUserId = getSearchParam('userId') || null;
  const isLicensed = getSearchParam('isLicensed') === 'true';
  const isClaimed = getSearchParam('isClaimed') === 'true';

  const { setPusherEvents } = usePusher(undefined, false, submitterEmail);

  const body: IOnboardingRequestBrokerageSetupApprovalBody = {
    submitterName,
    submitterEmail,
    // Can be undefined if request approval screen is opened in a different browser window
    // which usually happens during qa testing. We set a default value of empty string in these rare cases
    // since primaryRole is very insignificant and this will crash if undefined
    submitterRole: orgProfile?.primaryRole || '',
    submitterHash: verifyHash,
    ...(submitterUserId && { submitterUserId }),
  };

  useEffect(() => {
    const handlePusherEvent = () => {
      setVerified(curState => ({ ...curState, status: 'success' } as Verified));
    };
    setPusherEvents('onboardingApproval', 'approved', handlePusherEvent, true);
  }, [setPusherEvents]);

  useEffect(() => {
    const getApprover = async () => {
      try {
        const result = await OnboardingController.getBrokerageApprover({
          orgClaimHash,
        });
        setApprover(result);
      } catch (err) {
        setApproverError(true);
      }
    };

    const getVerifiedStatus = async () => {
      const result = await OnboardingController.getApprovalStatus({
        orgClaimHash,
        verifyHash,
      });
      setVerified(result);
    };

    const getData = async () => {
      await Promise.all([getApprover(), getVerifiedStatus()]);
      setLoading(false);
    };

    getData();
  }, [orgClaimHash, verifyHash]);

  const sendVerificationEmail = async (
    data: IOnboardingRequestBrokerageSetupApprovalBody,
  ) => {
    try {
      setEmailLoading(true);
      await OnboardingController.requestBrokerageSetupApproval(
        data,
        orgClaimHash,
      );
      setVerified({ status: 'pending', requestedAt: new Date() } as Verified);
      message.success('Verification sent');
    } catch (err) {
      message.error('Error sending verification');
    } finally {
      setEmailLoading(false);
    }
  };

  const VerificationStatusMap: Record<
    VerificationStatus,
    VerificationStatusStyle
  > = {
    [VerificationStatus.unsent]: {
      buttonText: 'Send Verification',
      onButtonClick: () => sendVerificationEmail(body),
      title: `Get approval from your ${approverTerm}`,
      alert: `An email will be sent to the ${approverTerm} for their approval at the address below. You'll be notified once it's approved.`,
    },
    [VerificationStatus.pending]: {
      buttonText: 'Resend Verification',
      onButtonClick: () => sendVerificationEmail(body),
      title: `Verify your ${approverTerm}`,
      alert: `An email has been sent to the ${approverTerm} for their approval. You'll be notified once they approve.`,
    },
    [VerificationStatus.success]: {
      buttonText: 'Next',
      onButtonClick: () => {
        handleGoNextStep(
          isClaimed ? UserOnboardingStep.TOS : UserOnboardingStep.PASSWORD,
        );
      },
      title: `Verify your ${approverTerm}`,
      alert: `An email has been sent to the ${approverTerm} for their approval. You'll be notified once they approve.`,
    },
  };

  const { title, alert, onButtonClick, buttonText } = {
    ...VerificationStatusMap[verified?.status],
  };

  if (loading) {
    return <LoadingView />;
  }
  if (approverError) {
    return <NoApproverView />;
  }

  return (
    <VerticalSpace>
      <CardHeader
        title={title}
        icon={<CardAvatar icon={SafetyCertificateFilled} />}
        onRequestStepBack={goBackStep(isLicensed && !isClaimed, verifyHash)}
      />

      <Alert message={alert} />
      {verified && approver && (
        <VerificationCard
          approverTerm={approverTerm}
          brokerage={approver.orgName}
          name={approver.name}
          email={approver.email}
          status={verified.status}
          lastSentDate={verified.status !== 'unsent' && verified.requestedAt}
        />
      )}

      <StyledButton
        data-cy="action-button"
        type="primary"
        disabled={emailLoading}
        icon={
          verified.status !== 'success' && (
            <MailFilled style={{ color: 'white' }} />
          )
        }
        onClick={onButtonClick}
      >
        {buttonText}
      </StyledButton>

      <RowCentered>
        <CardProgress percent={33} />
      </RowCentered>
    </VerticalSpace>
  );
};

const RowCentered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledButton = styled(Button)`
  width: 100%;
  font-weight: 400;
`;

export default RequestApprovalView;
